<script lang="ts" setup>
import TextInput from '@/components/inputs/TextInput.vue'
import OverlayBase from '@/components/overlays/OverlayBase.vue'
import ResponsiveImage from '@/components/ResponsiveImage.vue'
import TwoColsBlock from '@/components/layout/TwoColsBlock.vue'
import SkrHeading from '@/components/SkrHeading.vue'
import SkrButton from '@/components/button/SkrButton.vue'

const emit = defineEmits<{
  close: []
}>()

const { language } = storeToRefs(useLanguageStore())

const { createSnackbar } = useWidgetStore()

const { t } = useI18n()

const userStore = useUserStore()
const businessStore = useBusinessStore()

const { email: userEmail, hasSso } = storeToRefs(userStore)

const shouldUseEmailFlow = computed(() => hasSso.value)

const inputEmail = ref('')
const inputPassword = ref('')
const waitingForResponse = ref(false)

const disableSubmitButton = computed(() => {
  if (shouldUseEmailFlow.value) return userEmail.value !== inputEmail.value
  else return inputPassword.value === ''
})

const { userRepository } = useApi()

const deleteUser = async () => {
  waitingForResponse.value = true
  try {
    await userRepository.delete(shouldUseEmailFlow.value ? undefined : inputPassword.value)
    sessionStorage.setItem('userHasJustBeenDeleted', 'true')
    await userStore.logoutUser(`/bye?lang=${language.value}`)
  } catch (error: unknown) {
    if ((error as { response?: { status: number } }).response?.status === 403) {
      createSnackbar({
        message: t('global.password_incorrect'),
      })
      waitingForResponse.value = false
    } else await navigateTo({ name: 'sorry' })
  }
}

const deleteAccount = async () => {
  if (businessStore.isStarterPlan) {
    waitingForResponse.value = true
    // We only delete the business, as the user will be cleaned up afterwards automatically
    await businessStore.deleteBusiness()
    sessionStorage.setItem('userHasJustBeenDeleted', 'true')
    await userStore.logoutUser(`/bye?lang=${language.value}`)
    return
  }
  await deleteUser()
}

const boldText = computed(() => {
  if (shouldUseEmailFlow.value) return t('delete_account.text.bold_email', { email: userEmail.value })
  else return t('delete_account.text.bold_password')
})

const { mdAndUp } = useDisplay()
</script>

<template>
  <overlay-base>
    <two-cols-block class="fill-height" split="5/7">
      <template #left>
        <div class="mb-12">
          <skr-heading level="1" semantic>{{ t('delete_account.title') }}</skr-heading>
          <skr-heading level="2">
            {{ t('delete_account.text.upper') }}<br /><br /><strong>{{ boldText }}</strong></skr-heading
          >
        </div>
        <text-input
          v-if="shouldUseEmailFlow"
          v-model="inputEmail"
          data-cy="email_input"
          :label="t('delete_account.input.label_email')"
        />
        <text-input
          v-else
          v-model="inputPassword"
          type="password"
          data-cy="password_input"
          :label="t('delete_account.input.label_password')"
        />
        <skr-button
          data-cy="delete_account_confirmation_button"
          size="lg"
          color="error"
          block
          :disabled="disableSubmitButton"
          :loading="waitingForResponse"
          @click="deleteAccount"
        >
          {{ t('delete_account.cta.label') }}
        </skr-button>
        <div class="mt-4">
          <skr-button size="lg" color="grey-dark" type="text" block @click="emit('close')">
            {{ t('global.no_thanks') }}
          </skr-button>
        </div>
      </template>
      <template v-if="mdAndUp" #right>
        <responsive-image source="delete-skribble" :width="565" :height="405" />
      </template>
    </two-cols-block>
  </overlay-base>
</template>
